import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { MessageSquare, TrendingUp, RefreshCw, Eye, LogOut } from 'lucide-react';
import type { TopicSummary, BackAndForthPattern, ClusterSummary, DropoffPoint } from '../../types/journeys';

interface OverviewSectionProps {
  startingTopics: TopicSummary[];
  commonFollowups: TopicSummary[];
  backAndForthPatterns: BackAndForthPattern[];
  dropoffPoints: DropoffPoint[];
  onTopicClick: (cluster: ClusterSummary) => void;
  onViewExamples?: (clusterId: number) => void;
  onDropoffClick?: (clusterId: number) => void;
  explorationPath?: ClusterSummary[];
  loading?: boolean;
}

// Selection badges component - displays stacked badges for multiple selections
function SelectionBadges({
  orders,
  colorClass,
  position = 'right',
}: {
  orders: number[];
  colorClass: string;
  position?: 'left' | 'right';
}) {
  if (orders.length === 0) return null;

  // For a single selection, show a simple badge
  if (orders.length === 1) {
    return (
      <div
        className={`absolute -top-2 ${position === 'right' ? '-right-2' : '-left-2'} w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${colorClass} shadow-md z-10`}
      >
        {orders[0]}
      </div>
    );
  }

  // For multiple selections, show stacked badges with elegant offset
  return (
    <div className={`absolute -top-2 ${position === 'right' ? '-right-2' : '-left-2'} z-10`}>
      <div className="relative flex items-center">
        {orders.map((order, idx) => {
          // Calculate offset: badges fan out horizontally
          const offset = idx * 14; // 14px offset between badges
          return (
            <div
              key={`${order}-${idx}`}
              className={`absolute w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${colorClass} shadow-md border-2 border-white`}
              style={{
                transform: `translateX(${position === 'right' ? -offset : offset}px)`,
                zIndex: orders.length - idx, // First badge on top
              }}
            >
              {order}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Topic card component with hover preview and selection indicator
function TopicCard({
  topic,
  colorScheme,
  onTopicClick,
  onViewExamples,
  selectionOrders,
  formatNumber,
}: {
  topic: TopicSummary;
  colorScheme: 'blue' | 'green' | 'purple';
  onTopicClick: (cluster: ClusterSummary) => void;
  onViewExamples?: (clusterId: number) => void;
  selectionOrders: number[];
  formatNumber: (n: number) => string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const leaveTimeoutRef = useRef<NodeJS.Timeout>();
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout>();

  const colorClasses = {
    blue: {
      hover: 'hover:border-blue-300 hover:bg-blue-50/50',
      ring: 'ring-2 ring-blue-500 ring-offset-2',
      badge: 'bg-blue-500 text-white',
    },
    green: {
      hover: 'hover:border-green-300 hover:bg-green-50/50',
      ring: 'ring-2 ring-green-500 ring-offset-2',
      badge: 'bg-green-500 text-white',
    },
    purple: {
      hover: 'hover:border-purple-300 hover:bg-purple-50/50',
      ring: 'ring-2 ring-purple-500 ring-offset-2',
      badge: 'bg-purple-500 text-white',
    },
  };

  const colors = colorClasses[colorScheme];
  const isSelected = selectionOrders.length > 0;
  const hasExamples = topic.example_values && topic.example_values.length > 0;

  const handleMouseEnter = () => {
    clearTimeout(leaveTimeoutRef.current);
    clearTimeout(fadeOutTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        // Position to the right of the card, but check if it would overflow
        let left = rect.right + 8;
        const popoverWidth = 320; // w-80 = 20rem = 320px
        if (left + popoverWidth > window.innerWidth - 16) {
          // Position to the left instead
          left = rect.left - popoverWidth - 8;
        }
        setPopoverPosition({
          top: Math.max(8, rect.top),
          left: Math.max(8, left),
        });
        setIsHovered(true);
        // Trigger fade-in after mount
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
        });
      }
    }, 150); // Reduced from 300ms for faster response
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    clearTimeout(fadeOutTimeoutRef.current);
    leaveTimeoutRef.current = setTimeout(() => {
      // Start fade out
      setIsVisible(false);
      // Remove from DOM after animation completes
      fadeOutTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, 200);
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    clearTimeout(leaveTimeoutRef.current);
    clearTimeout(fadeOutTimeoutRef.current);
  };

  const handlePopoverMouseLeave = () => {
    leaveTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      fadeOutTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, 200);
    }, 150);
  };

  useEffect(() => {
    return () => {
      clearTimeout(hoverTimeoutRef.current);
      clearTimeout(leaveTimeoutRef.current);
      clearTimeout(fadeOutTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <div
        ref={cardRef}
        className={`relative p-3 rounded-lg border cursor-pointer transition-all ${colors.hover} ${
          isSelected ? colors.ring : ''
        }`}
        onClick={() => onTopicClick(topic.cluster)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Selection order badge(s) */}
        <SelectionBadges orders={selectionOrders} colorClass={colors.badge} />
        
        <div className="flex items-start justify-between">
          <span className="font-medium text-sm text-gray-900 line-clamp-2 pr-2">
            {topic.cluster.label}
          </span>
          <Badge variant="secondary" className="ml-2 text-xs shrink-0">
            {formatNumber(topic.conversation_count)}
          </Badge>
        </div>
        {hasExamples && (
          <p className="text-xs text-gray-500 mt-1 line-clamp-1">
            "{topic.example_values[0]}"
          </p>
        )}
      </div>

      {/* Hover popover - rendered via portal with fade animation */}
      {isHovered && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed z-[100] w-80 rounded-xl border border-gray-200/60 bg-white/95 backdrop-blur-sm p-4 text-gray-900"
          style={{
            top: popoverPosition.top,
            left: popoverPosition.left,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.96)',
            transition: 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1), transform 200ms cubic-bezier(0.16, 1, 0.3, 1)',
            boxShadow: isVisible 
              ? '0 20px 40px -12px rgba(0, 0, 0, 0.15), 0 8px 20px -8px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.03)'
              : '0 4px 12px -4px rgba(0, 0, 0, 0.1)',
          }}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          <div className="space-y-3">
            <div>
              <div className="font-semibold text-sm mb-1 text-gray-900">{topic.cluster.label}</div>
              <div className="text-xs text-gray-500">
                {formatNumber(topic.conversation_count)} conversations
              </div>
            </div>
            
            {hasExamples && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Example values</div>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {topic.example_values.slice(0, 5).map((example, idx) => (
                    <div
                      key={idx}
                      className="text-xs bg-gray-50 rounded-lg px-3 py-2 text-gray-600 line-clamp-2 border border-gray-100"
                      style={{
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? 'translateX(0)' : 'translateX(-4px)',
                        transition: `opacity 200ms cubic-bezier(0.16, 1, 0.3, 1) ${50 + idx * 30}ms, transform 200ms cubic-bezier(0.16, 1, 0.3, 1) ${50 + idx * 30}ms`,
                      }}
                    >
                      "{example}"
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {onViewExamples && (
              <Button
                size="sm"
                variant="outline"
                className="w-full mt-1 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(4px)',
                  transition: 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1) 100ms, transform 200ms cubic-bezier(0.16, 1, 0.3, 1) 100ms, background-color 150ms ease',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsVisible(false);
                  setTimeout(() => setIsHovered(false), 200);
                  onViewExamples(topic.cluster.id);
                }}
              >
                <Eye className="h-3.5 w-3.5 mr-1.5" />
                View All Examples
              </Button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// Drop-off card component with special styling for drop-off rates
function DropoffCard({
  dropoff,
  onClick,
  formatNumber,
}: {
  dropoff: DropoffPoint;
  onClick: (clusterId: number) => void;
  formatNumber: (n: number) => string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const leaveTimeoutRef = useRef<NodeJS.Timeout>();
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout>();

  const hasExamples = dropoff.example_values && dropoff.example_values.length > 0;

  // Color the rate based on severity (higher = more red/orange)
  const getRateColor = (rate: number) => {
    if (rate >= 70) return 'text-red-600 bg-red-50';
    if (rate >= 50) return 'text-orange-600 bg-orange-50';
    if (rate >= 30) return 'text-amber-600 bg-amber-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  const handleMouseEnter = () => {
    clearTimeout(leaveTimeoutRef.current);
    clearTimeout(fadeOutTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        let left = rect.right + 8;
        const popoverWidth = 320;
        if (left + popoverWidth > window.innerWidth - 16) {
          left = rect.left - popoverWidth - 8;
        }
        setPopoverPosition({
          top: Math.max(8, rect.top),
          left: Math.max(8, left),
        });
        setIsHovered(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
        });
      }
    }, 150);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    clearTimeout(fadeOutTimeoutRef.current);
    leaveTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      fadeOutTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, 200);
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    clearTimeout(leaveTimeoutRef.current);
    clearTimeout(fadeOutTimeoutRef.current);
  };

  const handlePopoverMouseLeave = () => {
    leaveTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      fadeOutTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, 200);
    }, 150);
  };

  useEffect(() => {
    return () => {
      clearTimeout(hoverTimeoutRef.current);
      clearTimeout(leaveTimeoutRef.current);
      clearTimeout(fadeOutTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <div
        ref={cardRef}
        className="relative p-3 rounded-lg border cursor-pointer transition-all hover:border-red-300 hover:bg-red-50/50"
        onClick={() => onClick(dropoff.cluster.id)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex items-start justify-between">
          <span className="font-medium text-sm text-gray-900 line-clamp-2 pr-2">
            {dropoff.cluster.label}
          </span>
          <Badge 
            variant="secondary" 
            className={`ml-2 text-xs shrink-0 font-semibold ${getRateColor(dropoff.dropoff_rate)}`}
          >
            {dropoff.dropoff_rate}%
          </Badge>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {formatNumber(dropoff.dropoff_count)} ended here
        </p>
      </div>

      {/* Hover popover */}
      {isHovered && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed z-[100] w-80 rounded-xl border border-gray-200/60 bg-white/95 backdrop-blur-sm p-4 text-gray-900"
          style={{
            top: popoverPosition.top,
            left: popoverPosition.left,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.96)',
            transition: 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1), transform 200ms cubic-bezier(0.16, 1, 0.3, 1)',
            boxShadow: isVisible 
              ? '0 20px 40px -12px rgba(0, 0, 0, 0.15), 0 8px 20px -8px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.03)'
              : '0 4px 12px -4px rgba(0, 0, 0, 0.1)',
          }}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          <div className="space-y-3">
            <div>
              <div className="font-semibold text-sm mb-1 text-gray-900">{dropoff.cluster.label}</div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className={`font-semibold px-1.5 py-0.5 rounded ${getRateColor(dropoff.dropoff_rate)}`}>
                  {dropoff.dropoff_rate}% drop-off
                </span>
                <span>{formatNumber(dropoff.total_visits)} total visits</span>
              </div>
            </div>
            
            {hasExamples && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Example values</div>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {dropoff.example_values.slice(0, 5).map((example, idx) => (
                    <div
                      key={idx}
                      className="text-xs bg-gray-50 rounded-lg px-3 py-2 text-gray-600 line-clamp-2 border border-gray-100"
                      style={{
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? 'translateX(0)' : 'translateX(-4px)',
                        transition: `opacity 200ms cubic-bezier(0.16, 1, 0.3, 1) ${50 + idx * 30}ms, transform 200ms cubic-bezier(0.16, 1, 0.3, 1) ${50 + idx * 30}ms`,
                      }}
                    >
                      "{example}"
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <Button
              size="sm"
              variant="outline"
              className="w-full mt-1 rounded-lg font-medium hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(4px)',
                transition: 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1) 100ms, transform 200ms cubic-bezier(0.16, 1, 0.3, 1) 100ms, background-color 150ms ease',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setIsVisible(false);
                setTimeout(() => setIsHovered(false), 200);
                onClick(dropoff.cluster.id);
              }}
            >
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              Investigate Drop-off
            </Button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default function OverviewSection({
  startingTopics,
  commonFollowups,
  backAndForthPatterns,
  dropoffPoints,
  onTopicClick,
  onViewExamples,
  onDropoffClick,
  explorationPath = [],
  loading = false,
}: OverviewSectionProps) {
  const formatNumber = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}K`
      : n.toString();

  // Get all selection orders for a cluster (1-indexed for display)
  // Returns array of all positions where cluster appears in the path
  const getSelectionOrders = (clusterId: number): number[] => {
    const orders: number[] = [];
    explorationPath.forEach((c, index) => {
      if (c.id === clusterId) {
        orders.push(index + 1);
      }
    });
    return orders;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">What People Do Most</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Starting Topics */}
          <Card 
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 0ms forwards',
            }}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-blue-500" />
                Starting Topics
              </CardTitle>
            </CardHeader>
            <CardContent>
              {startingTopics.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No starting topics found
                </p>
              ) : (
                <div className="space-y-3">
                  {startingTopics.slice(0, 5).map((topic, idx) => (
                    <div
                      key={topic.cluster.id}
                      style={{
                        opacity: 0,
                        animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${100 + idx * 60}ms forwards`,
                      }}
                    >
                      <TopicCard
                        topic={topic}
                        colorScheme="blue"
                        onTopicClick={onTopicClick}
                        onViewExamples={onViewExamples}
                        selectionOrders={getSelectionOrders(topic.cluster.id)}
                        formatNumber={formatNumber}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Common Follow-ups */}
          <Card 
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 100ms forwards',
            }}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                Common Follow-ups
              </CardTitle>
            </CardHeader>
            <CardContent>
              {commonFollowups.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No follow-up patterns found
                </p>
              ) : (
                <div className="space-y-3">
                  {commonFollowups.slice(0, 5).map((topic, idx) => (
                    <div
                      key={topic.cluster.id}
                      style={{
                        opacity: 0,
                        animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${150 + idx * 60}ms forwards`,
                      }}
                    >
                      <TopicCard
                        topic={topic}
                        colorScheme="green"
                        onTopicClick={onTopicClick}
                        onViewExamples={onViewExamples}
                        selectionOrders={getSelectionOrders(topic.cluster.id)}
                        formatNumber={formatNumber}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Back & Forth Patterns */}
          <Card 
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 200ms forwards',
            }}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-purple-500" />
                Back & Forth Patterns
              </CardTitle>
            </CardHeader>
            <CardContent>
              {backAndForthPatterns.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No back-and-forth patterns found
                </p>
              ) : (
                <div className="space-y-3">
                  {backAndForthPatterns.slice(0, 5).map((pattern, idx) => {
                    const selectionOrdersA = getSelectionOrders(pattern.cluster_a.id);
                    const selectionOrdersB = getSelectionOrders(pattern.cluster_b.id);
                    const isASelected = selectionOrdersA.length > 0;
                    const isBSelected = selectionOrdersB.length > 0;
                    
                    return (
                      <div
                        key={idx}
                        style={{
                          opacity: 0,
                          animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${200 + idx * 60}ms forwards`,
                        }}
                      >
                        <div
                          className={`relative p-3 rounded-lg border hover:border-purple-300 hover:bg-purple-50/50 cursor-pointer transition-all ${
                            isASelected || isBSelected ? 'ring-2 ring-purple-500 ring-offset-2' : ''
                          }`}
                          onClick={() => onTopicClick(pattern.cluster_a)}
                        >
                          {/* Selection order badge(s) for pattern A */}
                          <SelectionBadges
                            orders={selectionOrdersA}
                            colorClass="bg-purple-500 text-white"
                            position="left"
                          />
                          {/* Selection order badge(s) for pattern B */}
                          <SelectionBadges
                            orders={selectionOrdersB}
                            colorClass="bg-purple-500 text-white"
                            position="right"
                          />
                          
                          <div className="flex items-center gap-2 text-sm">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="font-medium text-gray-900 line-clamp-1">
                                  {pattern.cluster_a.label}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{pattern.cluster_a.label}</p>
                              </TooltipContent>
                            </Tooltip>
                            <span className="text-purple-500 shrink-0">&#x21C4;</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="font-medium text-gray-900 line-clamp-1">
                                  {pattern.cluster_b.label}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{pattern.cluster_b.label}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatNumber(pattern.conversation_count)} conversations
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Drop-off Points */}
          <Card 
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 300ms forwards',
            }}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <LogOut className="h-4 w-4 text-red-500" />
                Drop-off Points
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dropoffPoints.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No significant drop-off points found
                </p>
              ) : (
                <div className="space-y-3">
                  {dropoffPoints.slice(0, 5).map((dropoff, idx) => (
                    <div
                      key={dropoff.cluster.id}
                      style={{
                        opacity: 0,
                        animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${250 + idx * 60}ms forwards`,
                      }}
                    >
                      <DropoffCard
                        dropoff={dropoff}
                        onClick={onDropoffClick || (() => {})}
                        formatNumber={formatNumber}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  );
}
